import { useMemo, useState } from 'react'
import type { TaskSummary } from '../../../api/types'
import { Button } from '../../../components/ui/Button'
import styles from './TaskCalendarView.module.css'

const WEEKDAYS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So']

function toDateKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

export function TaskCalendarView({ tasks, onOpenTask }: { tasks: TaskSummary[]; onOpenTask: (id: string) => void }) {
  const [cursor, setCursor] = useState(() => {
    const now = new Date()
    return new Date(now.getFullYear(), now.getMonth(), 1)
  })

  const tasksByDay = useMemo(() => {
    const map = new Map<string, TaskSummary[]>()
    for (const task of tasks) {
      if (!task.dueDate) continue
      const key = toDateKey(new Date(task.dueDate))
      const list = map.get(key) ?? []
      list.push(task)
      map.set(key, list)
    }
    return map
  }, [tasks])

  const cells = useMemo(() => {
    const firstOfMonth = new Date(cursor.getFullYear(), cursor.getMonth(), 1)
    const startOffset = (firstOfMonth.getDay() + 6) % 7 // Montag = 0
    const gridStart = new Date(firstOfMonth)
    gridStart.setDate(gridStart.getDate() - startOffset)

    return Array.from({ length: 42 }, (_, i) => {
      const date = new Date(gridStart)
      date.setDate(gridStart.getDate() + i)
      return date
    })
  }, [cursor])

  return (
    <div>
      <div className={styles.header}>
        <Button variant="ghost" size="sm" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}>
          ← Zurück
        </Button>
        <span className={styles.monthLabel}>{cursor.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' })}</span>
        <Button variant="ghost" size="sm" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}>
          Weiter →
        </Button>
      </div>
      <div className={styles.grid}>
        {WEEKDAYS.map((day) => (
          <div key={day} className={styles.dayHeader}>
            {day}
          </div>
        ))}
        {cells.map((date) => {
          const key = toDateKey(date)
          const dayTasks = tasksByDay.get(key) ?? []
          const isCurrentMonth = date.getMonth() === cursor.getMonth()
          return (
            <div key={key} className={[styles.cell, isCurrentMonth ? '' : styles.cellMuted].join(' ')}>
              <span className={styles.dayNumber}>{date.getDate()}</span>
              {dayTasks.slice(0, 3).map((task) => (
                <span key={task.id} className={styles.taskChip} onClick={() => onOpenTask(task.id)} title={task.title}>
                  {task.title}
                </span>
              ))}
              {dayTasks.length > 3 ? <span className={styles.dayNumber}>+{dayTasks.length - 3} mehr</span> : null}
            </div>
          )
        })}
      </div>
    </div>
  )
}
